(function (root) {
  "use strict";

  var MIN_BPM = 20;
  var MAX_BPM = 300;
  var SAMPLE_RATES = Object.freeze([22050, 44100, 48000, 88200, 96000, 176400, 192000]);
  var TIME_SIGNATURES = Object.freeze([
    Object.freeze([2, 4]), Object.freeze([3, 4]), Object.freeze([4, 4]), Object.freeze([5, 4]),
    Object.freeze([6, 8]), Object.freeze([7, 8]), Object.freeze([9, 8]), Object.freeze([12, 8])
  ]);
  var BARS_MIN = 1;
  var BARS_MAX = 999;
  var NOTES = Object.freeze([
    Object.freeze({ division: "1/1", name: "Whole", quarterMultiplier: 4 }),
    Object.freeze({ division: "1/2", name: "Half", quarterMultiplier: 2 }),
    Object.freeze({ division: "1/4", name: "Quarter", quarterMultiplier: 1 }),
    Object.freeze({ division: "1/8", name: "Eighth", quarterMultiplier: 0.5 }),
    Object.freeze({ division: "1/16", name: "Sixteenth", quarterMultiplier: 0.25 }),
    Object.freeze({ division: "1/32", name: "Thirty-second", quarterMultiplier: 0.125 }),
    Object.freeze({ division: "1/64", name: "Sixty-fourth", quarterMultiplier: 0.0625 })
  ]);
  var VARIANTS = Object.freeze([
    Object.freeze({ id: "straight", multiplier: 1 }),
    Object.freeze({ id: "dotted", multiplier: 1.5 }),
    Object.freeze({ id: "triplet", multiplier: 2 / 3 })
  ]);

  function failure(code, message) {
    return { ok: false, error: { code: code, message: message } };
  }
  function success(value) { return Object.assign({ ok: true }, value); }
  function finite(value) { return typeof value === "number" && Number.isFinite(value); }
  function validateBpm(value, field) {
    field = field || "bpm";
    if (!finite(value) || value <= 0) return failure("invalid_" + field, field + " must be a finite positive number.");
    if (value < MIN_BPM || value > MAX_BPM) return failure(field + "_out_of_range", field + " must be between 20 and 300 BPM.");
    return success({ value: value });
  }
  function validateSampleRate(value, field) {
    field = field || "sampleRate";
    if (!finite(value)) return failure("invalid_" + field, field + " must be a supported sample rate.");
    if (SAMPLE_RATES.indexOf(value) === -1) return failure("unsupported_" + field, field + " must be one of the supported sample rates.");
    return success({ value: value });
  }
  function validateMeter(numerator, denominator) {
    if (!Number.isInteger(numerator) || !Number.isInteger(denominator)) {
      return failure("invalid_time_signature", "Time signature must be one of the supported numerator/denominator pairs.");
    }
    var supported = TIME_SIGNATURES.some(function (signature) { return signature[0] === numerator && signature[1] === denominator; });
    if (!supported) return failure("unsupported_time_signature", "Time signature must be one of 2/4, 3/4, 4/4, 5/4, 6/8, 7/8, 9/8, or 12/8.");
    return success({ numerator: numerator, denominator: denominator, quarterNotesPerBar: numerator * (4 / denominator) });
  }
  function validateBars(bars) {
    if (!Number.isInteger(bars)) return failure("invalid_bars", "Bar count must be an integer from 1 to 999.");
    if (bars < BARS_MIN || bars > BARS_MAX) return failure("bars_out_of_range", "Bar count must be an integer from 1 to 999.");
    return success({ bars: bars });
  }
  function validateLength(numerator, denominator, bars) {
    var meter = validateMeter(numerator, denominator);
    if (!meter.ok) return meter;
    var barValidation = validateBars(bars);
    if (!barValidation.ok) return barValidation;
    return success({
      numerator: numerator,
      denominator: denominator,
      bars: bars,
      quarterNotesPerBar: meter.quarterNotesPerBar,
      quarterNotesTotal: meter.quarterNotesPerBar * bars
    });
  }
  function durationFor(bpm, quarterNotes) { return quarterNotes * 60 / bpm; }
  function frameValues(durationSeconds, sampleRate) {
    var exactFrames = durationSeconds * sampleRate;
    var frameCount = Math.round(exactFrames);
    return { exactFrames: exactFrames, frameCount: frameCount };
  }
  function calculateLoop(input) {
    if (!input || typeof input !== "object" || Array.isArray(input)) return failure("invalid_input", "Input must be an object.");
    var bpmCheck = validateBpm(input.bpm);
    if (!bpmCheck.ok) return bpmCheck;
    var rateCheck = validateSampleRate(input.sampleRate);
    if (!rateCheck.ok) return rateCheck;
    var length = validateLength(input.numerator, input.denominator, input.bars);
    if (!length.ok) return length;

    var quarterNoteSeconds = 60 / input.bpm;
    var barDurationSeconds = durationFor(input.bpm, length.quarterNotesPerBar);
    var durationSeconds = durationFor(input.bpm, length.quarterNotesTotal);
    var barFrames = frameValues(barDurationSeconds, input.sampleRate);
    var loopFrames = frameValues(durationSeconds, input.sampleRate);
    return success({
      bpm: input.bpm,
      sampleRate: input.sampleRate,
      numerator: input.numerator,
      denominator: input.denominator,
      bars: input.bars,
      quarterNotesPerBar: length.quarterNotesPerBar,
      quarterNotesTotal: length.quarterNotesTotal,
      quarterNoteDurationSeconds: quarterNoteSeconds,
      quarterNoteDurationMilliseconds: quarterNoteSeconds * 1000,
      barDurationSeconds: barDurationSeconds,
      barDurationMilliseconds: barDurationSeconds * 1000,
      exactBarFrames: barFrames.exactFrames,
      barFrameCount: barFrames.frameCount,
      durationSeconds: durationSeconds,
      durationMilliseconds: durationSeconds * 1000,
      exactFrames: loopFrames.exactFrames,
      frameCount: loopFrames.frameCount,
      frameRoundingError: loopFrames.frameCount - loopFrames.exactFrames
    });
  }
  function noteSamplesTable(input) {
    if (!input || typeof input !== "object" || Array.isArray(input)) return failure("invalid_input", "Input must be an object.");
    var bpmCheck = validateBpm(input.bpm);
    if (!bpmCheck.ok) return bpmCheck;
    var rateCheck = validateSampleRate(input.sampleRate);
    if (!rateCheck.ok) return rateCheck;
    var quarterSeconds = 60 / input.bpm;
    var rows = NOTES.map(function (note) {
      var values = {};
      VARIANTS.forEach(function (variant) {
        var durationSeconds = quarterSeconds * note.quarterMultiplier * variant.multiplier;
        var frames = frameValues(durationSeconds, input.sampleRate);
        values[variant.id] = {
          durationSeconds: durationSeconds,
          durationMilliseconds: durationSeconds * 1000,
          exactFrames: frames.exactFrames,
          frameCount: frames.frameCount
        };
      });
      return { division: note.division, name: note.name, values: values };
    });
    return success({ bpm: input.bpm, sampleRate: input.sampleRate, rows: rows });
  }
  function tempoInterpretations(bpm, numerator, denominator, bars) {
    var choices = [
      { id: "base", bpm: bpm, bars: bars },
      { id: "half-time", bpm: bpm / 2, bars: bars / 2 },
      { id: "double-time", bpm: bpm * 2, bars: bars * 2 }
    ];
    return choices.map(function (choice) {
      var wholeBarCount = Number.isInteger(choice.bars) && choice.bars >= BARS_MIN && choice.bars <= BARS_MAX;
      var bpmSupported = choice.bpm >= MIN_BPM && choice.bpm <= MAX_BPM;
      return {
        interpretation: choice.id,
        bpm: choice.bpm,
        bars: choice.bars,
        quarterNotesPerBar: numerator * (4 / denominator),
        available: choice.id === "base" || wholeBarCount,
        bpmSupported: bpmSupported,
        supported: wholeBarCount && bpmSupported,
        reason: !wholeBarCount ? "bar_count_unavailable" : (!bpmSupported ? "bpm_out_of_range" : null)
      };
    }).filter(function (choice) { return choice.available; });
  }
  function findBpmFromDuration(input) {
    if (!input || typeof input !== "object" || Array.isArray(input)) return failure("invalid_input", "Input must be an object.");
    if (!finite(input.durationSeconds) || input.durationSeconds <= 0) return failure("invalid_duration", "Duration must be a finite positive number of seconds.");
    var length = validateLength(input.numerator, input.denominator, input.bars);
    if (!length.ok) return length;
    var bpm = length.quarterNotesTotal * 60 / input.durationSeconds;
    if (!Number.isFinite(bpm) || bpm < MIN_BPM || bpm > MAX_BPM) return failure("bpm_out_of_range", "The inferred BPM must be between 20 and 300.");
    return success({
      durationSeconds: input.durationSeconds,
      bpm: bpm,
      numerator: input.numerator,
      denominator: input.denominator,
      bars: input.bars,
      quarterNotesPerBar: length.quarterNotesPerBar,
      quarterNotesTotal: length.quarterNotesTotal,
      interpretations: tempoInterpretations(bpm, input.numerator, input.denominator, input.bars)
    });
  }
  function findBpmFromFrames(input) {
    if (!input || typeof input !== "object" || Array.isArray(input)) return failure("invalid_input", "Input must be an object.");
    if (!Number.isInteger(input.frameCount) || input.frameCount <= 0) return failure("invalid_frame_count", "Frame count must be a positive integer.");
    var rateCheck = validateSampleRate(input.sourceSampleRate, "sourceSampleRate");
    if (!rateCheck.ok) return rateCheck;
    var result = findBpmFromDuration({
      durationSeconds: input.frameCount / input.sourceSampleRate,
      numerator: input.numerator,
      denominator: input.denominator,
      bars: input.bars
    });
    if (!result.ok) return result;
    result.frameCount = input.frameCount;
    result.sourceSampleRate = input.sourceSampleRate;
    return result;
  }
  function matchTempo(input) {
    if (!input || typeof input !== "object" || Array.isArray(input)) return failure("invalid_input", "Input must be an object.");
    var sourceCheck = validateBpm(input.sourceBpm, "source_bpm");
    if (!sourceCheck.ok) return sourceCheck;
    var targetCheck = validateBpm(input.targetBpm, "target_bpm");
    if (!targetCheck.ok) return targetCheck;
    var length = validateLength(input.numerator, input.denominator, input.bars);
    if (!length.ok) return length;
    var sourceDurationSeconds = durationFor(input.sourceBpm, length.quarterNotesTotal);
    var targetDurationSeconds = durationFor(input.targetBpm, length.quarterNotesTotal);
    var ratio = input.targetBpm / input.sourceBpm;
    return success({
      sourceBpm: input.sourceBpm,
      targetBpm: input.targetBpm,
      numerator: input.numerator,
      denominator: input.denominator,
      bars: input.bars,
      quarterNotesTotal: length.quarterNotesTotal,
      sourceDurationSeconds: sourceDurationSeconds,
      targetDurationSeconds: targetDurationSeconds,
      durationPercentage: input.sourceBpm / input.targetBpm * 100,
      playbackRatePercentage: ratio * 100,
      repitchSemitones: 12 * Math.log2(ratio)
    });
  }

  /**
   * DOM-independent calculator API. Each calculation accepts one input object
   * and returns { ok: true, ...values } or { ok: false, error: { code, message } }.
   * BPM is quarter-notes per minute; frameCount is Math.round(exactFrames).
   * Tempo alternatives preserve measured duration by halving/doubling both BPM
   * and bar count. An alternative outside BPM support remains listed with
   * supported:false; alternatives with invalid bar counts are omitted.
   */
  root.AudiocolourBpmSamplesCore = Object.freeze({
    supportedSampleRates: SAMPLE_RATES,
    supportedTimeSignatures: TIME_SIGNATURES,
    supportedNoteDivisions: NOTES,
    supportedVariants: VARIANTS,
    validateBpm: function (bpm) { return validateBpm(bpm); },
    calculateLoop: calculateLoop,
    noteSamplesTable: noteSamplesTable,
    findBpmFromDuration: findBpmFromDuration,
    findBpmFromFrames: findBpmFromFrames,
    tempoInterpretations: tempoInterpretations,
    matchTempo: matchTempo
  });
}(globalThis));
